import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Home, Sparkles, MessageSquare, User, Calendar, Share2,
  LayoutDashboard, Compass, Plus, Users as UsersIcon, DollarSign, Inbox,
} from "lucide-react";
import { useAuth } from "../lib/auth";

/**
 * MobileBottomNav — sticky bottom tab bar for mobile, modelled on the
 * "Always-Visible AI Plan Button" mockup.
 *
 * Layout per role (5 tabs, with the **center** slot rendering as an elevated
 * coral FAB that sits slightly above the bar):
 *
 *   • Customer  → Home / Explore / [PLAN]  / Bookings / Messages
 *   • Concierge → Dashboard / Requests / [CREATE] / Bookings / Messages
 *   • Host      → Dashboard / Guests / [SHARE] / Earnings / Profile
 *   • Admin     → Dashboard / Users / [PLAN] / Messages / Account
 *   • LoggedOut → Explore / How / [PLAN] / About / Sign in
 *
 * The center action is the heart of TREL — it routes directly into the AI
 * Concierge box (or its role-specific equivalent) so the user can describe
 * what they want without hunting through menus. See the asset mockup in the
 * Phase 40 prompt for the visual reference (elevated coral pill with a
 * sparkles icon, soft glow ring, sits 6-10px above the nav baseline).
 *
 * Hidden on desktop (`lg:hidden`) and on top-level marketing routes ("/",
 * "/login", "/signup") so it doesn't compete with the hero CTAs.
 */

// Each entry: { to, label, icon }. The CENTER tab additionally gets
// `center: true` + an optional `centerIcon` override. We separate the center
// tab into a dedicated `center` field on the config so the layout component
// can position it absolutely without splicing arrays at render time.
const CFG = {
  customer: {
    side: [
      { to: "/customer",          label: "Home",     icon: Home },
      { to: "/explore",           label: "Explore",  icon: Compass },
      { to: "/customer#bookings", label: "Bookings", icon: Calendar },
      { to: "/messages",          label: "Messages", icon: MessageSquare },
    ],
    center: { to: "/", label: "Plan", icon: Sparkles },
  },
  provider: {
    side: [
      { to: "/provider",              label: "Dashboard", icon: LayoutDashboard },
      { to: "/provider#requests",     label: "Requests",  icon: Inbox },
      { to: "/provider#bookings",     label: "Bookings",  icon: Calendar },
      { to: "/messages",              label: "Messages",  icon: MessageSquare },
    ],
    // Phase 43-B + Core Concierge Logic: replace the customer-style "Create"
    // FAB with the TREL+ AI Business Assistant entry point. The button deep
    // links to the AIExperienceAssistant block on the provider dashboard
    // (it surfaces an "Upgrade to TREL+" locked preview if the concierge
    // isn't on the active plan).
    center: { to: "/provider#ai-assistant", label: "AI Tools", icon: Sparkles },
  },
  host: {
    side: [
      { to: "/host",            label: "Dashboard", icon: LayoutDashboard },
      { to: "/host#guests",     label: "Guests",    icon: UsersIcon },
      { to: "/host#earnings",   label: "Earnings",  icon: DollarSign },
      { to: "/account",         label: "Profile",   icon: User },
    ],
    center: { to: "/host#share", label: "Share", icon: Share2 },
  },
  admin: {
    side: [
      { to: "/admin",            label: "Dashboard", icon: LayoutDashboard },
      { to: "/admin?tab=users",  label: "Users",     icon: UsersIcon },
      { to: "/messages",         label: "Messages",  icon: MessageSquare },
      { to: "/account",          label: "Account",   icon: User },
    ],
    center: { to: "/", label: "Plan", icon: Sparkles },
  },
  loggedOut: {
    side: [
      { to: "/explore", label: "Explore", icon: Compass },
      { to: "/how",     label: "How",     icon: Sparkles },
      { to: "/about",   label: "About",   icon: User },
      { to: "/login",   label: "Sign in", icon: User },
    ],
    center: { to: "/", label: "Plan", icon: Sparkles },
  },
};

export default function MobileBottomNav() {
  const { user } = useAuth();
  const loc = useLocation();
  const pathname = loc.pathname;
  // Include hash so two tabs that point to the same route never both highlight.
  const currentPath = `${loc.pathname}${loc.hash || ""}`;

  // Hide the bottom nav on top-level public marketing pages so it doesn't
  // visually compete with the hero CTAs.
  const HIDDEN_ROUTES = ["/", "/login", "/signup"];
  if (HIDDEN_ROUTES.includes(pathname)) return null;

  const role = user?.role && CFG[user.role] ? user.role : "loggedOut";
  const { side, center } = CFG[role];

  // Two-pass active matcher: hash/query-bearing tabs win over pathname-only
  // siblings (so /customer#bookings highlights Bookings, not Home).
  const allTabs = [...side, center];
  const hashMatch = allTabs.find(
    (tab) => (tab.to.includes("#") || tab.to.includes("?")) && tab.to === currentPath
  );
  const pathMatch = allTabs.find(
    (tab) => !tab.to.includes("#") && !tab.to.includes("?") && pathname === tab.to
  );
  const activeTo = hashMatch?.to ?? pathMatch?.to;

  const CenterIcon = center.icon;

  return (
    <nav
      data-testid="mobile-bottom-nav"
      className="lg:hidden fixed bottom-0 inset-x-0 z-40 pb-[env(safe-area-inset-bottom)]"
    >
      {/* Bar surface — pure white, soft top border, soft elevation. */}
      <div className="relative bg-white border-t border-[#071A2D]/10 shadow-[0_-6px_24px_-12px_rgba(7,26,45,0.16)]">
        {/* 4-up side tabs (2 left + 2 right). The visual center column is
            reserved for the elevated coral FAB which floats above. */}
        <ul className="grid grid-cols-5 text-[10px] uppercase tracking-[0.14em] font-semibold">
          {side.slice(0, 2).map((tab) => (
            <SideTab key={tab.to} tab={tab} active={tab.to === activeTo} />
          ))}

          {/* Center column — reserves space; the actual FAB is positioned absolute. */}
          <li className="relative">
            <NavLink
              to={center.to}
              data-testid={`mobile-nav-${center.label.toLowerCase().replace(/\s+/g, "-")}`}
              aria-label={center.label}
              className="block"
            >
              <span
                className={`absolute left-1/2 -translate-x-1/2 -top-6 w-14 h-14 rounded-full
                            bg-[#FF7A45] text-white flex items-center justify-center
                            shadow-[0_10px_24px_-8px_rgba(255,122,69,0.65)]
                            ring-4 ring-white
                            transition-transform duration-200 ease-out
                            ${center.to === activeTo ? "scale-105" : "hover:scale-105 active:scale-95"}`}
              >
                <CenterIcon size={22} strokeWidth={2.4}/>
              </span>
              <span className="block pt-9 pb-2 text-center text-[10px] uppercase tracking-[0.14em] font-semibold text-[#FF7A45]">
                {center.label}
              </span>
            </NavLink>
          </li>

          {side.slice(2, 4).map((tab) => (
            <SideTab key={tab.to} tab={tab} active={tab.to === activeTo} />
          ))}
        </ul>
      </div>
    </nav>
  );
}

function SideTab({ tab, active }) {
  const { to, label, icon: Icon } = tab;
  return (
    <li className="text-center">
      <NavLink
        to={to}
        data-testid={`mobile-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
        className={`flex flex-col items-center justify-center gap-1 py-2.5 ${
          active ? "text-[#064E3B]" : "text-[#071A2D]/60 hover:text-[#071A2D]"
        }`}
      >
        <Icon size={18}/>
        <span className={active ? "text-[#064E3B]" : ""}>{label}</span>
        <span
          className={`block w-6 h-[2px] rounded-full transition-all ${
            active ? "bg-[#C9A45C]" : "bg-transparent"
          }`}
        />
      </NavLink>
    </li>
  );
}
